'use client';

import * as React from "react";
import { Film, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * PeekPlayer — Standard-mode lecture-into-lab bidirectional companion.
 *
 * MV5 Figma page `24 · Peek + Mini-IDE · MV5` (`127:56`). Five states from
 * the P1-P5 spec:
 *
 *   P1 chip · resting          — the "peek at lecture" affordance in the AI
 *                                pane; a compact chip with the lecture title.
 *   P2 corner overlay compact  — 640×360 corner PIP; hover reveals controls.
 *   P3 overlay expanded        — 900×540 centered overlay; more chrome.
 *   P4 full-screen takeover    — the lecture takes over the whole viewport
 *                                (Standard mode's "just show me the video").
 *   P5 empty chip              — no lecture context available (lab has no
 *                                associated lecture); disabled state.
 *
 * State machine (see the interaction matrix H2 on the Figma page):
 *
 *   chip → compact overlay (default open)
 *   compact ↔ expanded (toggle via expand button)
 *   any overlay → full (double-click on video, or the maximize icon)
 *   full → expanded (Esc, or the minimize icon)
 *   any overlay → chip (close button, or Esc on compact)
 *
 * Only one overlay is open at a time — the caller (Learning Item Shell)
 * enforces this by closing the sibling MiniIDE when this opens.
 *
 * The player itself is a slot: `renderPlayer` receives the current state
 * and returns the actual <video> / <iframe>. This keeps the primitive
 * player-agnostic so we can reuse it with hls.js in web-ide and with a
 * mock in Storybook.
 */

export type PeekPlayerState = "chip" | "compact" | "expanded" | "full" | "empty";

export interface PeekPlayerProps {
  state: PeekPlayerState;
  onStateChange: (next: PeekPlayerState) => void;
  /** Lecture title shown on the chip and in the overlay header. */
  title?: string;
  /** Optional subtitle (e.g. course name, chapter). */
  subtitle?: string;
  /** Slot: the actual video element. Rendered in compact/expanded/full states. */
  renderPlayer?: (state: Exclude<PeekPlayerState, "chip" | "empty">) => React.ReactNode;
  /**
   * Chip click handler. Defaults to transitioning to `compact`. Callers may
   * override to open the overlay in a specific state (e.g. straight to full).
   */
  onOpen?: () => void;
  /** Whether to render as a mobile-optimized bottom sheet. Defaults to auto based on viewport. */
  mobile?: boolean;
  /** Custom label for the chip's empty state. */
  emptyLabel?: string;
  className?: string;
}

const OVERLAY_STATES: PeekPlayerState[] = ["compact", "expanded", "full"];

function isOverlay(s: PeekPlayerState): s is "compact" | "expanded" | "full" {
  return OVERLAY_STATES.includes(s);
}

// ── P1/P5 Chip ─────────────────────────────────────────────────────────────
const PeekChip: React.FC<{
  title?: string;
  disabled?: boolean;
  onClick?: () => void;
  emptyLabel?: string;
}> = ({ title, disabled, onClick, emptyLabel }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    aria-disabled={disabled}
    disabled={disabled}
    data-slot="peek-chip"
    data-state={disabled ? "empty" : "resting"}
    className={cn(
      "inline-flex items-center gap-space-8 h-[32px] px-space-12 rounded-chip",
      "border text-caption-12 font-medium",
      "transition-colors duration-micro ease-standard",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
      disabled
        ? "border-chrome-border bg-chrome-panel text-ink-muted cursor-not-allowed"
        : "border-accent-violet/40 bg-accent-violet/10 text-ink-primary hover:bg-accent-violet/20",
    )}
  >
    <Film className="size-4 shrink-0" />
    <span className="max-w-[220px] truncate">
      {disabled ? emptyLabel ?? "No lecture linked" : title ?? "Peek at lecture"}
    </span>
  </button>
);

// ── P2 / P3 / P4 Overlay ───────────────────────────────────────────────────
const PeekOverlay: React.FC<{
  state: "compact" | "expanded" | "full";
  onStateChange: (next: PeekPlayerState) => void;
  title?: string;
  subtitle?: string;
  renderPlayer?: (state: "compact" | "expanded" | "full") => React.ReactNode;
  mobile?: boolean;
}> = ({ state, onStateChange, title, subtitle, renderPlayer, mobile }) => {
  // Escape key: full → expanded, expanded/compact → chip.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (state === "full") onStateChange("expanded");
      else onStateChange("chip");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onStateChange]);

  const sizeClass: Record<typeof state, string> = {
    compact: mobile
      ? "fixed inset-x-0 bottom-0 h-[45vh] rounded-t-card"
      : "fixed bottom-space-24 end-space-24 w-[640px] max-w-[calc(100vw-48px)] aspect-video rounded-card shadow-2xl",
    expanded: mobile
      ? "fixed inset-x-0 bottom-0 h-[70vh] rounded-t-card"
      : "fixed inset-0 m-auto w-[900px] max-w-[calc(100vw-64px)] h-fit max-h-[calc(100vh-64px)] rounded-card shadow-2xl",
    full: "fixed inset-0 rounded-none",
  };

  const nextExpand: PeekPlayerState = state === "compact" ? "expanded" : "full";
  const nextShrink: PeekPlayerState = state === "full" ? "expanded" : state === "expanded" ? "compact" : "compact";

  return (
    <div
      role="dialog"
      aria-label={title ? `Peek player: ${title}` : "Peek player"}
      data-slot="peek-overlay"
      data-state={state}
      className={cn(
        "z-[60] bg-chrome-surface border border-chrome-border",
        "flex flex-col overflow-hidden",
        sizeClass[state],
      )}
    >
      {/* Header — always shown except in P2 compact where it's on hover */}
      <div
        className={cn(
          "flex items-center justify-between px-space-12 py-space-8 border-b border-chrome-border",
          "bg-chrome-panel/80 backdrop-blur-sm",
          state === "compact" && !mobile && "opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity",
        )}
      >
        <div className="min-w-0 flex-1">
          <p className="text-caption-12 text-ink-primary truncate">{title ?? "Lecture"}</p>
          {subtitle && (
            <p className="text-caption-12 text-ink-muted truncate">{subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-space-4 shrink-0">
          {state !== "compact" && (
            <button
              type="button"
              onClick={() => onStateChange(nextShrink)}
              aria-label={state === "full" ? "Restore" : "Shrink to compact"}
              className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
            >
              <Minimize2 className="size-4" />
            </button>
          )}
          {state !== "full" && (
            <button
              type="button"
              onClick={() => onStateChange(nextExpand)}
              aria-label={state === "compact" ? "Expand" : "Full screen"}
              className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
            >
              <Maximize2 className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={() => onStateChange("chip")}
            aria-label="Close peek player"
            className="p-space-4 rounded-chip text-ink-muted hover:text-ink-primary hover:bg-chrome-surface"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-black">
        {renderPlayer ? (
          renderPlayer(state)
        ) : (
          <div className="w-full h-full flex items-center justify-center text-caption-12 text-ink-muted">
            No player rendered
          </div>
        )}
      </div>
    </div>
  );
};

export const PeekPlayer = React.forwardRef<HTMLDivElement, PeekPlayerProps>(
  function PeekPlayer(
    { state, onStateChange, title, subtitle, renderPlayer, onOpen, mobile, emptyLabel, className },
    ref,
  ) {
    const openChip = React.useCallback(() => {
      onOpen ? onOpen() : onStateChange("compact");
    }, [onOpen, onStateChange]);

    if (state === "chip" || state === "empty") {
      return (
        <div ref={ref} className={className} data-slot="peek-player" data-state={state}>
          <PeekChip
            title={title}
            disabled={state === "empty"}
            onClick={openChip}
            emptyLabel={emptyLabel}
          />
        </div>
      );
    }

    if (isOverlay(state)) {
      return (
        <div ref={ref} className={className} data-slot="peek-player" data-state={state}>
          <PeekOverlay
            state={state}
            onStateChange={onStateChange}
            title={title}
            subtitle={subtitle}
            renderPlayer={renderPlayer}
            mobile={mobile}
          />
        </div>
      );
    }

    return null;
  },
);

PeekPlayer.displayName = "PeekPlayer";
